import React, { useMemo } from 'react';
import { ActivityHistoryCard } from '@europrocurement/flexy-components';
import { UtilisateurTaches } from '@europrocurement/l2d-domain';
import { useTheme } from '@mui/material';
import { addNoteIcon, addTaskIcon, forbiddenIcon, preview } from '@europrocurement/l2d-icons';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import { PrescripteurTachesApiObject } from '@europrocurement/l2d-domain/reducers/tiers/slices/prescripteurTachesSlice';
import useTasksHook from '@europrocurement/l2d-modules/modules/Tasks/hook/useTasksHook';
import TasksCardContent from '@europrocurement/l2d-modules/modules/Tasks/components/TasksCardContent/TasksCardContent';
import ActivitiesCardContent from '@europrocurement/l2d-modules/modules/Dossiers/components/detail/ActivitiesCardContent/ActivitiesCardContent';
import usePublisherActivitiesHook from '@europrocurement/l2d-modules/modules/Fournisseurs/hooks/usePublisherActivitiesHook';
import { useCurrentPublisherCtx } from '@europrocurement/l2d-modules/modules/Fournisseurs/providers/CurrentPublisherProvider';

interface PublisherActivityTabProps {
    showTask?: boolean;
    showTechniqueLogs?: boolean;
    noteMemo?: boolean;
    showNotes?: boolean;
}

const PublisherActivityTab: React.FunctionComponent<PublisherActivityTabProps> = function ({
    showTask = true,
    showNotes = true,
    showTechniqueLogs = false,
    noteMemo = true,
}) {
    const { palette } = useTheme();
    const { publisher } = useCurrentPublisherCtx();
    const [showLogsTechniques, setShowLogsTechniques] = React.useState(false);

    const { activities, activitiesLoading, handleAddNote, handleEditNote, handleDeleteNote } =
        usePublisherActivitiesHook({ useMemoClient: noteMemo });

    const keycloakService = UseKeycloakService();
    const userIdentifier = keycloakService.getTokenParsed()?.preferred_username;
    const {
        orderedTasks,
        tasksLoading,
        handleAddTask,
        handleCheckTask,
        handleEditTask,
        handleDeleteTask,
    } = useTasksHook({
        apiTarget: 'tiers',
        linkedEntityId: publisher ? (publisher.id?.toString() as string) : undefined,
    });

    const handleLoadTechniques = React.useCallback(() => {
        setShowLogsTechniques((prev) => !prev);
    }, []);

    const textLogsTechnique = showLogsTechniques
        ? 'Masquer actions techniques'
        : 'Afficher actions techniques';

    const showIconTechniques = showLogsTechniques ? forbiddenIcon : preview;

    const actions = useMemo(
        () =>
            [
                showNotes && {
                    id: 'add-note',
                    label: 'Ajouter une note',
                    shortLabel: 'Ajouter',
                    icon: addNoteIcon,
                    onClick: handleAddNote,
                    variant: 'text' as const,
                    color: palette.grey[700],
                },
                showTask && {
                    id: 'add-task',
                    label: 'Ajouter une tâche',
                    shortLabel: 'Ajouter',
                    icon: addTaskIcon,
                    onClick: handleAddTask,
                    variant: 'text' as const,
                    color: palette.grey[700],
                },
                showTechniqueLogs && {
                    id: 'show-technique',
                    label: textLogsTechnique,
                    shortLabel: 'Afficher',
                    icon: showIconTechniques,
                    onClick: handleLoadTechniques,
                    variant: 'text' as const,
                    color: palette.grey[700],
                },
            ].filter((action): action is Exclude<typeof action, false> => action !== false),
        [
            showNotes,
            handleAddNote,
            palette.grey,
            showTask,
            handleAddTask,
            showTechniqueLogs,
            textLogsTechnique,
            showIconTechniques,
            handleLoadTechniques,
        ],
    );

    React.useEffect(() => {
        console.log('THERE IS A PUBLISHER IN THE CONTEXT !!', publisher);
    }, [publisher]);

    return (
        <ActivityHistoryCard actions={actions}>
            {showTask && publisher && (
                <TasksCardContent
                    tasks={orderedTasks as PrescripteurTachesApiObject[] | UtilisateurTaches[]}
                    tasksLoading={tasksLoading}
                    onCheckTask={handleCheckTask}
                    onEditTask={handleEditTask}
                    onDeleteTask={handleDeleteTask}
                    userIdentifier={userIdentifier}
                />
            )}

            <ActivitiesCardContent
                activities={activities}
                onEditNote={handleEditNote}
                onDeleteNote={handleDeleteNote}
                activitiesLoading={activitiesLoading}
            />
        </ActivityHistoryCard>
    );
};

export default PublisherActivityTab;
